<template>
  <SoErrorBoundary>
    <SoDialog
      v-model="isDialogShown"
      :loading="areAgreementsFetching"
      card-title="Agreements Comparison"
    >
      <SoTariffComparison :agreements="tariffComparisonTableData" />
    </SoDialog>
  </SoErrorBoundary>
  <SoWidget
    v-for="(meterpoint, index) in account.meterpoints.data"
    :key="index"
    :data-testid="`agreement-container-${index}`"
    class="h-auto mb-8"
  >
    <MeterpointHeader :meterpoint="meterpoint" class="mb-2" />
    <SoDataTable
      v-model="selectedItems[index]"
      :headers="agreementTableHeaders"
      :items="agreementTableData[meterpoint.identifier]"
      :sort-by="[{ key: 'fromDate', order: 'desc' }]"
      selectable
      multiple
      select-key="juniferAgreementNumber"
      row-clickable
      show-copy-to-clipboard
    >
      <template #tableActions>
        <SoButton
          title="Compare Agreements"
          large
          :disabled="!selectedItems[index] || !selectedItems[index]?.length"
          @click="showAgreementComparison(selectedItems[index])"
        />
      </template>
    </SoDataTable>
  </SoWidget>
  <SoWidget
    v-for="meterpoint in account.unavailableMeterpoints.data"
    :key="meterpoint.id"
    class="h-auto mb-8"
  >
    <MeterpointHeader :meterpoint="meterpoint" class="mb-2" />
    <UnavailableMeterpointAlert :meterpoint="meterpoint" />
  </SoWidget>
  <SoAlert
    v-if="
      account.meterpoints.isFetched &&
      !account.meterpoints.data.length &&
      !account.unavailableMeterpoints.data.length
    "
    type="warning"
    title="No Agreements Found"
  />
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import {
    SoDataTableColumnType,
    SoDataTableHeader,
    SoDataTableItem,
  } from "@/types/components/SoDataTable"
  import SoErrorBoundary from "@/components/SoErrorBoundary.vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoTariffComparison from "@/components/SoTariffComparison.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoWidget from "@/components/SoWidget.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import MeterpointHeader from "../../components/MeterpointHeader.vue"
  import UnavailableMeterpointAlert from "../../components/UnavailableMeterpointAlert.vue"
  import { IMeterPointAgreement } from "@/models/meterpointAgreement/meterpointAgreement.types"

  const headers: SoDataTableHeader[] = [
    {
      title: "Agreement",
      key: "juniferAgreementNumber",
    },
    {
      title: "Status",
      key: "status",
      type: SoDataTableColumnType.STATUS,
    },
    {
      title: "Name",
      key: "productName",
    },
    {
      title: "Reference",
      key: "productCode",
    },
    {
      title: "From",
      key: "fromDate",
      type: SoDataTableColumnType.DATE,
    },
    {
      title: "To",
      key: "toDate",
      type: SoDataTableColumnType.DATE,
    },
  ]

  export default defineComponent({
    components: {
      SoDataTable,
      SoButton,
      SoDialog,
      SoTariffComparison,
      SoErrorBoundary,
      SoWidget,
      SoAlert,
      MeterpointHeader,
      UnavailableMeterpointAlert,
    },
    inheritAttrs: false,
    data() {
      return {
        agreementTableHeaders: headers,
        account: useCurrentAccountStore(),
        selectedItems: [] as [][],
        isDialogShown: false,
        comparisonIds: [] as string[],
      }
    },
    computed: {
      agreementTableData() {
        const agreements = this.account.agreementsByMeterPoint.data
        const agreementsTableData: Record<string, SoDataTableItem[]> = {}

        Object.keys(agreements).forEach((key: string) => {
          if (agreements[key]) {
            agreementsTableData[key] = agreements[key].map((agreement) => {
              return {
                ...agreement,
                status: {
                  value: agreement.status.text,
                  props: { status: agreement.status },
                },
                productName: agreement.productDetails?.displayName,
                productCode: agreement.productDetails?.code,
              }
            })
          } else {
            agreementsTableData[key] = []
          }
        })
        return agreementsTableData
      },
      areAgreementsFetching() {
        return this.account.agreementsByMeterPoint.isFetching
      },
      tariffComparisonTableData(): Array<IMeterPointAgreement> {
        const agreements = this.account.agreementsByMeterPoint.data
        const filteredAgreements: IMeterPointAgreement[] = []

        Object.values(agreements).forEach((agreementsArray) => {
          agreementsArray.forEach((agreement) => {
            if (this.comparisonIds.includes(agreement.juniferAgreementNumber)) {
              filteredAgreements.push(agreement)
            }
          })
        })

        return filteredAgreements
      },
    },
    methods: {
      showAgreementComparison(selectedItems: Array<string>) {
        if (selectedItems && selectedItems.length > 0) {
          this.comparisonIds = selectedItems
          this.isDialogShown = true
        }
      },
    },
  })
</script>
